import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Chip,
    CircularProgress
} from '@mui/material';
import {
    Person,
    Phone,
} from '@mui/icons-material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const Agente = ({
    endpointPath = "/Agente",
    idProduto = 2,
    useClienteParams = true
}) => {

    const [agenteData, setAgenteData] = useState({
        nome: "Nome não disponível",
        telefone: "Telefone não disponível",
        acessoConta: "N/A",
    });
    const [isLoading, setIsLoading] = useState(false);

    const getQualityColor = (quality) => {
        switch (quality) {
            case "Alta":
                return "#4CAF50"; // Verde
            case "Média":
                return "#FF9800"; // Laranja
            case "Baixa":
                return "#F44336"; // Vermelho
            default:
                return "#9E9E9E"; // Cinza para N/A
        }
    };

    useEffect(() => {
        const qualityMap = {
            GREEN: "Alta",
            YELLOW: "Média",
            RED: "Baixa",
            UNKNOWN: "Pendente"
        };

        const fetchAgenteData = async () => {
            setIsLoading(true);
            try {
                const token = localStorage.getItem('jwtToken');
                if (!token) {
                    console.error("Token não encontrado no localStorage.");
                    return;
                }

                const baseURL = process.env.REACT_APP_API_BASE_URL;

                const requestConfig = {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                };

                if (useClienteParams) {
                    const decodedToken = jwtDecode(token);
                    requestConfig.params = {
                        idClienteAipim: decodedToken.id_cliente,
                        idProduto
                    };
                }

                const response = await axios.get(`${baseURL}${endpointPath}`, requestConfig);

                const responseData = response.data;

                // Função para formatar o telefone
                const formatTelefone = (numero) => {
                    if (!numero) return "Telefone não disponível";

                    // Remove tudo que não é número
                    const digits = numero.replace(/\D/g, "");

                    // Confere se tem DDI (ex: 55) - 12 dígitos
                    if (digits.length === 12 && digits.startsWith("55")) {
                        const ddi = digits.slice(0, 2);      // 55
                        const ddd = digits.slice(2, 4);      // ex: 79
                        const parte1 = digits.slice(4, 8);   // ex: 8171
                        const parte2 = digits.slice(8);      // ex: 9873
                        return `+${ddi} (${ddd}) ${parte1}-${parte2}`;
                    }

                    // Confere se tem DDI com 9 dígitos no número - 13 dígitos
                    if (digits.length === 13 && digits.startsWith("55")) {
                        const ddi = digits.slice(0, 2);      // 55
                        const ddd = digits.slice(2, 4);      // ex: 79
                        const parte1 = digits.slice(4, 9);   // ex: 99999
                        const parte2 = digits.slice(9);      // ex: 9999
                        return `+${ddi} (${ddd}) ${parte1}-${parte2}`;
                    }

                    return numero; // retorna como está se não bater o formato esperado
                };

                // Uso no mappedAgenteData
                const mappedAgenteData = {
                    nome: responseData.metaProfile?.verifiedName || responseData.agente?.nome || "Nome não disponível",
                    telefone: formatTelefone(responseData.metaProfile?.phoneNumber || responseData.whatsappBusinessPhone || responseData.agente?.telefone),
                    acessoConta: qualityMap[responseData.metaProfile?.qualityRating] || "N/A",
                };


                console.log("Dados do agente:", mappedAgenteData);
                setAgenteData(mappedAgenteData);

            } catch (error) {
                console.error("Erro ao buscar dados do agente:", error);

                // Manter dados padrão em caso de erro
                const defaultAgenteData = {
                    nome: "Nome não disponível",
                    telefone: "Telefone não disponível",
                    acessoConta: "N/A",
                };
                setAgenteData(defaultAgenteData);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAgenteData();
    }, [endpointPath, idProduto, useClienteParams]);

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    maxHeight: '100vh'
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                maxHeight: '100vh',
                padding: { xs: 1, sm: 2 }
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    width: { xs: '100%', sm: 'fit-content' },
                    maxWidth: '100%',
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                    boxShadow: (theme) =>
                        theme.palette.mode === 'dark'
                            ? '0 4px 16px rgba(0,0,0,0.28)'
                            : '0 4px 12px rgba(0,0,0,0.12)',
                    padding: { xs: 2, sm: 3 },
                    gap: { xs: 2, sm: 3 },
                    alignItems: 'center'
                }}
            >
                {/* Informações */}
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        width: { xs: '100%', sm: '100%' },
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2,
                        alignItems: 'center',
                        flexWrap: { xs: 'wrap', sm: 'nowrap' },
                        justifyContent: 'space-between'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: { xs: 'center', sm: 'flex-start' },
                            minWidth: { sm: 'max-content' }
                        }}
                    >
                        <Person sx={{ fontSize: { xs: 16, sm: 18 }, mr: 1, color: 'text.secondary' }} />
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                fontWeight: 'bold',
                                color: 'text.primary',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {agenteData.nome}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: { xs: 'center', sm: 'flex-start' }
                        }}
                    >
                        <Phone sx={{ fontSize: { xs: 16, sm: 18 }, mr: 1, color: 'text.secondary' }} />
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: { xs: '0.75rem', sm: '0.9rem' },
                                fontWeight: 'bold',
                                color: 'text.primary'
                            }}
                        >
                            {agenteData.telefone}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 1, sm: 0 },
                            justifyContent: { xs: 'center', sm: 'flex-start' }
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: { xs: 0, sm: 0 }
                            }}
                        >
                            <WhatsAppIcon sx={{ fontSize: { xs: 16, sm: 18 }, mr: 1, color: '#25D366' }} />
                            <Typography
                                variant="body1"
                                sx={{
                                    fontSize: { xs: '0.7rem', sm: '0.9rem' },
                                    fontWeight: 'bold',
                                    color: 'text.primary',
                                    mr: { xs: 0, sm: 1 }
                                }}
                            >
                                Qualidade WhatsApp:
                            </Typography>
                        </Box>
                        <Chip
                            label={agenteData.acessoConta}
                            size="small"
                            sx={{
                                height: { xs: 20, sm: 24 },
                                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                bgcolor: getQualityColor(agenteData.acessoConta),
                                color: 'white',
                                fontWeight: 'bold',
                            }}
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default Agente;
